/**
 * E002-T016: StreamLain Types
 *
 * Zentrale Type-Definitionen für StreamLain Module.
 */

import type { TriggerType } from '@streamlain/automation-safety-contract';

export type {
  AutomationConfig,
  AutomationOutputMode,
} from './services/automation';
export type {
  AutomationCausation,
  AutomationLoopDiagnostic,
  AutomationTopologyState,
  AutomationTopologyVerdict,
  HttpTarget,
  TriggerType,
} from '@streamlain/automation-safety-contract';
// Re-export filter types from data-view
export type {
  ExecutionMode,
  ExecutionProgress,
  ExecutionResult,
  ExecutionState,
  ExecutionStatus,
} from './services/execution';
export type {
  ExecutionLog,
  InputStageLog,
  OutputStageLog,
  ProcessStageLog,
  WorkflowNodeStep,
} from './services/logging';
export type TriggerStatus = 'active' | 'paused' | 'inactive';

// Aligned with backend CronPreset (workflow-engine.interface.ts)
export type CronPreset =
  | 'every_1_min'
  | 'every_5_min'
  | 'every_15_min'
  | 'every_30_min'
  | 'hourly'
  | 'daily_9am'
  | 'daily_6pm'
  | 'weekly_monday_9am'
  | 'monthly_first_9am';

export interface TriggerConfig {
  type: TriggerType;
  enabled: boolean;
  cronPreset?: CronPreset;
  timezone?: string;
  debounceMs?: number;
  nextRunAt?: number;
  /**
   * Webhook credential ID this automation listens to.
   * Only meaningful when type === 'webhook'.
   * If null/undefined: no inbound webhook is bound.
   * If set: workspace webhook from Workspace credentials, RBAC-scoped via WorkspaceCredential.
   */
  webhookCredentialId?: string | null;
}

export interface TriggerState {
  isActive: boolean;
  type: TriggerType;
  nextRunAt: number | null;
  lastTriggeredAt: number | null;
  status: TriggerStatus;
}

export const DEFAULT_TRIGGER_CONFIG: TriggerConfig = {
  type: 'manual',
  enabled: false,
  cronPreset: 'hourly',
  debounceMs: 5000,
};

/** Virtual inputTableId for webhook payload mappings in InputMapping[]. */
export const WEBHOOK_PAYLOAD_TABLE_ID = '__webhook_payload__';
/** Virtual columnId that represents the full webhook JSON body. */
export const WEBHOOK_PAYLOAD_COLUMN_ID = 'payload';

// Aligned with backend cron-calculator.service.ts
export const CRON_PRESETS: Record<
  CronPreset,
  { label: string; shortLabel: string; expression: string }
> = {
  every_1_min: {
    label: 'Every minute',
    shortLabel: '1 min',
    expression: '* * * * *',
  },
  every_5_min: {
    label: 'Every 5 minutes',
    shortLabel: '5 min',
    expression: '*/5 * * * *',
  },
  every_15_min: {
    label: 'Every 15 minutes',
    shortLabel: '15 min',
    expression: '*/15 * * * *',
  },
  every_30_min: {
    label: 'Every 30 minutes',
    shortLabel: '30 min',
    expression: '*/30 * * * *',
  },
  hourly: {
    label: 'Every hour',
    shortLabel: 'Hourly',
    expression: '0 * * * *',
  },
  daily_9am: {
    label: 'Daily at 9:00 AM',
    shortLabel: '9 AM',
    expression: '0 9 * * *',
  },
  daily_6pm: {
    label: 'Daily at 6:00 PM',
    shortLabel: '6 PM',
    expression: '0 18 * * *',
  },
  weekly_monday_9am: {
    label: 'Weekly Monday 9:00 AM',
    shortLabel: 'Weekly',
    expression: '0 9 * * 1',
  },
  monthly_first_9am: {
    label: 'Monthly 1st at 9:00 AM',
    shortLabel: 'Monthly',
    expression: '0 9 1 * *',
  },
};
export type { WorkflowField, WorkflowInfo } from './services/workflow';
export type {
  Filter,
  FilterGroup,
  SingleFilter,
} from '@blocksuite/affine/data-view';
